import { formatSignupUser, formatSignupCompany } from 'Formatters/signupFormatter'
import { signupCompany } from 'Api/companyApi'
import { signupUser } from 'Api/userApi'
import SignupState from 'Types/_Store/Signup'
import { MutationTree, ActionTree } from 'vuex'
import { StoreState } from 'Types'
import { parsePlanNew, parsePeriod, updatePeriodList } from 'Parsers/subscriptionParser'
import { getPlans, getPeriods } from 'Api/subscriptionApi'
import { AppPlan } from 'Services/appPlanList'
import planContainerTypes from 'Enums/planContainerTypes'

const state: SignupState = {
  requesting: {
    signupUser: false,
    signupCompany: false,
  },

  plan: null,

  user : {
    name: '',
    occupation: 0,
    otherOccupation: '',
    email: '',
    password: '',
    address: {
      description: '',
      type: 0,
      zipcode: '',
      cityId: 0,
      stateId: 0,
      neighborhood: '',
      street: '',
      streetNumber: '',
      streetDetails: '',
    },
  },

  company: {
    name: '',
    registry: '',
    planId: 0,
    periodId: 0,
    leadFrom: 1,
    user: {
      name: '',
      occupation: 0,
      otherOccupation: '',
      email: '',
      password: '',
    },
    phone: {
      ddd: '',
      number: '',
    },
    address: {
      description: '',
      type: null,
      zipcode: '',
      cityId: 0,
      stateId: 0,
      neighborhood: '',
      street: '',
      streetNumber: '',
      streetDetails: '',
    },
  }
}

const mutations: MutationTree<SignupState> = {
  request_start(state, type) {
    state.requesting[type] = true
  },
  request_success(state, type) {
    state.requesting[type] = false
  },
  request_failure(state, type) {
    state.requesting[type] = false
  },

  set_signup_plan(state, plan) {
    state.plan = plan
  },
  set_lead_from(state, leadFrom) {
    state.company.leadFrom = leadFrom
  },
  set_signup_user(state, user) {
    Object.assign(state.user, user)
  },
  set_signup_user_address(state, address) {
    Object.assign(state.user.address, address)
  },

  set_signup_company(state, company) {
    Object.assign(state.company, company)
  },
  set_signup_company_user(state, user) {
    Object.assign(state.company.user, user)
  },
  set_signup_company_phone(state, phone) {
    Object.assign(state.company.phone, phone)
  },
  set_signup_company_address(state, address) {
    Object.assign(state.company.address, address)
  },
}

const actions: ActionTree<SignupState, StoreState> = {
  async signupUser({ state, commit }) {
    commit('request_start', 'signupUser')

    try {
      const user = formatSignupUser(state.user)
      const result = await signupUser(user)

      commit('request_success', 'signupUser')

      return result
    } catch (err) {
      commit('request_failure', 'signupUser')

      throw err
    }
  },

  async signupCompany({ state, commit }, isLogged?) {
    commit('request_start', 'signupCompany')

    try {
      const company = formatSignupCompany(state.company)
      if (isLogged) {
        delete company.user
      }
      const result = await signupCompany(company, isLogged)

      commit('request_success', 'signupCompany')

      return result
    } catch (err) {
      commit('request_failure', 'signupCompany')

      throw err
    }
  },

  setLeadFrom({ commit }, leadFrom) {
    commit('set_lead_from', leadFrom)
  },

  async getPlanInfo({ commit }, planSelected: {planId: number, periodId: number} ): Promise<AppPlan> {
    const { planId,  periodId } = planSelected
    const [ planResult, periodResult ] = await Promise.all([
      getPlans(),
      getPeriods(),
    ])


    const { items: planList } = planResult.data

    const apiPlan = planId === null
      ? planList.find(plan => plan.container === planContainerTypes.demand)
      : planList.find(plan => plan.id === planId)

    const updatedPlans = updatePeriodList(periodId, apiPlan)

    const appPlan = parsePlanNew(updatedPlans)

    const { data: periodList } = periodResult
    const apiPeriod = periodList.find(item => item.id === periodId)
    const appPeriod = parsePeriod(apiPeriod)

    const selectedPlan = appPlan.withPeriod(appPeriod)

    commit('set_signup_plan', selectedPlan)

    commit('set_signup_company', {
      planId: selectedPlan.id,
      periodId: selectedPlan.period.id,
    })

    return appPlan
  },

  updateUser({ commit }, user) {
    commit('set_signup_user', user)
  },
  updateUserAddress({ commit }, address) {
    commit('set_signup_user_address', address)
  },

  updateCompany({ commit }, company) {
    commit('set_signup_company', company)
  },
  updateCompanyUser({ commit }, user) {
    commit('set_signup_company_user', user)
  },
  updateCompanyPhone({ commit }, phone) {
    commit('set_signup_company_phone', phone)
  },
  updateCompanyAddress({ commit }, address) {
    commit('set_signup_company_address', address)
  },
}

export default {
  namespaced: true,

  state,
  mutations,
  actions,
}